import { z } from 'zod';
import { calendarDateSchema, countryCodeSchema, instantSchema, latitudeSchema, longitudeSchema, nonEmptyTextSchema, sha256Schema, uuidSchema, } from './primitives.js';
import { revisionMetadataSchema, sourceSnapshotSchema } from './versioning.js';
export const locationIdentifierNamespaceSchema = z.enum([
    'SR70',
    'RIL100',
    'PLC',
    'RINF_UOPID',
    'UIC',
    'OTHER',
]);
export const locationOperationalStatusSchema = z.enum([
    'active',
    'inactive',
    'planned',
    'temporary',
    'unknown',
]);
export const locationTypeSchema = z.enum([
    'station',
    'station_district',
    'halt',
    'junction',
    'passing_loop',
    'block_post',
    'border_point',
    'freight_station',
    'loading_point',
    'terminal',
    'siding_connection',
    'depot',
    'yard',
    'signal_box',
    'operational_point',
    'other',
]);
export const sourceClaimSchema = z
    .strictObject({
    claim_id: uuidSchema,
    claim_type: z.enum([
        'name',
        'short_name',
        'location_type',
        'coordinate',
        'operator',
        'operational_status',
        'validity',
        'identifier',
    ]),
    value: z.string().min(1).max(2_048),
    source: sourceSnapshotSchema,
    valid_from: calendarDateSchema.nullable(),
    valid_to: calendarDateSchema.nullable(),
    observed_at: instantSchema,
})
    .superRefine((value, context) => {
    if (value.valid_from && value.valid_to && value.valid_to < value.valid_from) {
        context.addIssue({
            code: 'custom',
            path: ['valid_to'],
            message: 'valid_to must not precede valid_from',
        });
    }
});
export const locationIdentifierSchema = z
    .strictObject({
    location_identifier_id: uuidSchema,
    location_id: uuidSchema,
    namespace: locationIdentifierNamespaceSchema,
    namespace_other: z.string().min(1).max(128).nullable(),
    value: z.string().min(1).max(256),
    publisher: z.string().min(1).max(256),
    valid_from: calendarDateSchema.nullable(),
    valid_to: calendarDateSchema.nullable(),
    operational_status: locationOperationalStatusSchema,
    snapshot_version: z.string().min(1).max(128),
    snapshot_sha256: sha256Schema,
    binding_method: z.enum(['direct', 'authoritative_crosswalk', 'reviewed_match']),
})
    .superRefine((value, context) => {
    if ((value.namespace === 'OTHER') !== (value.namespace_other !== null)) {
        context.addIssue({
            code: 'custom',
            path: ['namespace_other'],
            message: 'namespace_other is required only for OTHER',
        });
    }
    if (value.valid_from && value.valid_to && value.valid_to < value.valid_from) {
        context.addIssue({
            code: 'custom',
            path: ['valid_to'],
            message: 'valid_to must not precede valid_from',
        });
    }
});
export const identifierReferenceSchema = z
    .strictObject({
    namespace: locationIdentifierNamespaceSchema,
    namespace_other: z.string().min(1).max(128).nullable(),
    value: z.string().min(1).max(256),
})
    .superRefine((value, context) => {
    if ((value.namespace === 'OTHER') !== (value.namespace_other !== null)) {
        context.addIssue({
            code: 'custom',
            path: ['namespace_other'],
            message: 'namespace_other is required only for OTHER',
        });
    }
});
export const authoritativeCrosswalkSchema = z
    .strictObject({
    crosswalk_id: uuidSchema,
    left: identifierReferenceSchema,
    right: identifierReferenceSchema,
    publisher: z.string().min(1).max(256),
    source: sourceSnapshotSchema,
    valid_from: calendarDateSchema.nullable(),
    valid_to: calendarDateSchema.nullable(),
})
    .superRefine((value, context) => {
    if (sameIdentifier(value.left, value.right)) {
        context.addIssue({ code: 'custom', message: 'crosswalk endpoints must differ' });
    }
    if (value.valid_from && value.valid_to && value.valid_to < value.valid_from) {
        context.addIssue({
            code: 'custom',
            path: ['valid_to'],
            message: 'valid_to must not precede valid_from',
        });
    }
});
export const coordinateSchema = z.strictObject({
    latitude: latitudeSchema,
    longitude: longitudeSchema,
    accuracy_metres: z.number().finite().nonnegative().nullable(),
    source_claim_id: uuidSchema,
});
export const locationSchema = z
    .strictObject({
    location_id: uuidSchema,
    official_name: nonEmptyTextSchema,
    short_name: nonEmptyTextSchema.nullable(),
    country_code: countryCodeSchema,
    region: z.string().max(256).nullable(),
    municipality: z.string().max(256).nullable(),
    location_type: locationTypeSchema,
    coordinate: coordinateSchema.nullable(),
    aliases: z.array(nonEmptyTextSchema).max(256),
    historical_names: z.array(nonEmptyTextSchema).max(256),
    search_variants: z.array(nonEmptyTextSchema).max(512),
    operator: z.string().max(256).nullable(),
    valid_from: calendarDateSchema.nullable(),
    valid_to: calendarDateSchema.nullable(),
    operational_status: locationOperationalStatusSchema,
    origin: z.enum(['official', 'user_defined']),
    identifiers: z.array(locationIdentifierSchema).max(128),
    source_claims: z.array(sourceClaimSchema).max(512),
    metadata: revisionMetadataSchema,
})
    .superRefine((value, context) => {
    const identifierIds = new Set();
    const identifierKeys = new Set();
    for (const [index, identifier] of value.identifiers.entries()) {
        if (identifier.location_id !== value.location_id) {
            context.addIssue({
                code: 'custom',
                path: ['identifiers', index, 'location_id'],
                message: 'identifier must belong to its containing location',
            });
        }
        const key = `${identifier.namespace}\u0000${identifier.namespace_other ?? ''}\u0000${identifier.value}`;
        if (identifierIds.has(identifier.location_identifier_id) || identifierKeys.has(key)) {
            context.addIssue({
                code: 'custom',
                path: ['identifiers', index],
                message: 'duplicate location identifier',
            });
        }
        identifierIds.add(identifier.location_identifier_id);
        identifierKeys.add(key);
    }
    const claimsById = new Map(value.source_claims.map((claim) => [claim.claim_id, claim]));
    if (claimsById.size !== value.source_claims.length) {
        context.addIssue({
            code: 'custom',
            path: ['source_claims'],
            message: 'source claim IDs must be unique within a location',
        });
    }
    if (value.coordinate) {
        const claim = claimsById.get(value.coordinate.source_claim_id);
        if (!claim || claim.claim_type !== 'coordinate') {
            context.addIssue({
                code: 'custom',
                path: ['coordinate', 'source_claim_id'],
                message: 'coordinate must reference an included coordinate source claim',
            });
        }
    }
    if (value.valid_from && value.valid_to && value.valid_to < value.valid_from) {
        context.addIssue({
            code: 'custom',
            path: ['valid_to'],
            message: 'valid_to must not precede valid_from',
        });
    }
    if (value.origin === 'official' && value.source_claims.length === 0) {
        context.addIssue({
            code: 'custom',
            path: ['source_claims'],
            message: 'official locations require source claims',
        });
    }
});
export const locationSnapshotIdentifierSchema = identifierReferenceSchema;
export const locationSnapshotSchema = z
    .strictObject({
    location_id: uuidSchema,
    display_name: nonEmptyTextSchema,
    location_type: locationTypeSchema,
    country_code: countryCodeSchema,
    key_identifiers: z.array(locationSnapshotIdentifierSchema).max(32),
})
    .superRefine((value, context) => {
    const keys = value.key_identifiers.map((identifier) => `${identifier.namespace}\u0000${identifier.namespace_other ?? ''}\u0000${identifier.value}`);
    if (new Set(keys).size !== keys.length) {
        context.addIssue({
            code: 'custom',
            path: ['key_identifiers'],
            message: 'snapshot identifiers must be unique by namespace and value',
        });
    }
});
export const locationMergeDecisionSchema = z
    .strictObject({
    decision_id: uuidSchema,
    source_location_id: uuidSchema,
    target_location_id: uuidSchema,
    basis: z.enum(['same_namespace_value', 'authoritative_crosswalk', 'reviewed_match']),
    crosswalk_id: uuidSchema.nullable(),
    automatic: z.boolean(),
    reviewed_by: z.string().min(1).max(256).nullable(),
    reviewed_at: instantSchema.nullable(),
    metadata: revisionMetadataSchema,
})
    .superRefine((value, context) => {
    if (value.source_location_id === value.target_location_id) {
        context.addIssue({
            code: 'custom',
            path: ['target_location_id'],
            message: 'cannot merge a location into itself',
        });
    }
    if (value.automatic && value.basis === 'reviewed_match') {
        context.addIssue({
            code: 'custom',
            path: ['automatic'],
            message: 'reviewed matches cannot be automatic',
        });
    }
    if ((value.basis === 'authoritative_crosswalk') !== (value.crosswalk_id !== null)) {
        context.addIssue({
            code: 'custom',
            path: ['crosswalk_id'],
            message: 'crosswalk_id is required only for authoritative crosswalk merges',
        });
    }
    if (value.basis === 'reviewed_match' &&
        (value.reviewed_by === null || value.reviewed_at === null)) {
        context.addIssue({
            code: 'custom',
            message: 'reviewed matches require reviewer and timestamp',
        });
    }
});
export function canAutomaticallyMergeIdentifiers(leftInput, rightInput, crosswalksInput) {
    const left = identifierReferenceSchema.parse(leftInput);
    const right = identifierReferenceSchema.parse(rightInput);
    const crosswalks = z.array(authoritativeCrosswalkSchema).parse(crosswalksInput);
    const sameNamespace = left.namespace === right.namespace &&
        left.namespace_other === right.namespace_other &&
        left.value === right.value;
    if (sameNamespace) {
        return { allowed: true, basis: 'same_namespace_value', crosswalk_id: null };
    }
    const match = crosswalks.find((crosswalk) => (sameIdentifier(crosswalk.left, left) && sameIdentifier(crosswalk.right, right)) ||
        (sameIdentifier(crosswalk.left, right) && sameIdentifier(crosswalk.right, left)));
    return match
        ? { allowed: true, basis: 'authoritative_crosswalk', crosswalk_id: match.crosswalk_id }
        : { allowed: false, basis: 'none', crosswalk_id: null };
}
function sameIdentifier(left, right) {
    return (left.namespace === right.namespace &&
        left.namespace_other === right.namespace_other &&
        left.value === right.value);
}
//# sourceMappingURL=location.js.map